import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Upload, FileText, CheckCircle, Trash2, Save, Send, Download, Eye, Star, MapPin, XCircle, LogIn, MessageCircle, Pencil, KeyRound } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ApprovedDriver } from '../types/user';
import { driverService } from '../services/driverService';
import { documentService } from '../services/documentService';
import { userService } from '../services/userService';
import { corporateCarrierService, type CorporateCarrier } from '../services/corporateCarrierService';
import { vehicleService } from '../services/vehicleService';
import { useAppAlert } from '../components/AppAlertProvider';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';
import { FlexibleDataGrid, type FlexibleDataGridColumn } from '../components/FlexibleDataGrid';
import { exportRowsToExcel } from '../utils/excelExport';
import { authService } from '../services/authService';

interface DriverLocation {
  driver_id: number;
  shipment_id?: number | null;
  latitude: number;
  longitude: number;
  speed?: number | null;
  heading?: number | null;
  recorded_at: string;
  shipment_no?: string | null;
}

const DOCUMENT_LABELS: Record<string, string> = {
  'tax-plate': 'Vergi Levhası',
  'license': 'Ehliyet',
  'tractor-license': 'Çekici Ruhsat',
  'trailer-license': 'Dorse Ruhsat',
  'src': 'SRC Belgesi',
  'vehicle-insurance': 'Araç Sigortası',
  'contract': 'Taşıma Sözleşmesi',
  'carrier-authority': 'Taşıyıcı Yetki Belgesi',
  'psychotechnic': 'Psikoteknik Belgesi',
};

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://') || pathStr.startsWith('data:')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

const normalizePhone = (value?: string | null) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const normalizeUpper = (value?: string | null) => String(value || '').trim().toLocaleUpperCase('tr-TR');
const normalizePlate = (value?: string | null) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const isValidPlate = (value?: string | null) => {
  const plate = normalizePlate(value);

  return (
    !plate ||
    (plate.length >= 6 &&
      plate.length <= 9 &&
      /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate))
  );
};

type CarrierVehicleOption = { id: string; plate: string; trailerPlate: string; vehicleType: string };

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  'active': { label: 'Onaylı', color: 'text-green-700', bg: 'bg-green-100' },
  'inactive': { label: 'Reddedildi', color: 'text-red-700', bg: 'bg-red-100' },
  'suspended': { label: 'Beklemede', color: 'text-yellow-700', bg: 'bg-yellow-100' },
};

export function ApprovedDrivers() {
  const { showAlert } = useAppAlert();
  const { showConfirm } = useAppAlert();
  const { user: currentUser, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [drivers, setDrivers] = useState<ApprovedDriver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<ApprovedDriver | null>(null);
  const [startEditingDriverId, setStartEditingDriverId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const corporateCompanyId = hasUserRole(currentUser, 'corporate-external-transport', 'corporate-external-transport-user') ? String((currentUser as any)?.externalTransportAccountUserId || currentUser?.id || '').trim() : '';
  const companyIdFilter = corporateCompanyId || String(searchParams.get('companyId') || '').trim();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Record<string, DriverLocation>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkMessageModalOpen, setBulkMessageModalOpen] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState('');
  const [bulkMessageChannel, setBulkMessageChannel] = useState<'sms' | 'whatsapp'>('sms');
  const [bulkMessageSending, setBulkMessageSending] = useState(false);
  const [manualLocationModal, setManualLocationModal] = useState<{ driver: ApprovedDriver; latitude: string; longitude: string; saving: boolean } | null>(null);
  const [carrierCompanies, setCarrierCompanies] = useState<CorporateCarrier[]>([]);
  const [docsPanelDriver, setDocsPanelDriver] = useState<ApprovedDriver | null>(null);
  const [docsPanelDocs, setDocsPanelDocs] = useState<any[]>([]);
  const [docsPanelLoading, setDocsPanelLoading] = useState(false);
  const [docsPanelUploading, setDocsPanelUploading] = useState(false);

  const openDocsPanel = async (driver: ApprovedDriver) => {
    setDocsPanelDriver(driver);
    setDocsPanelLoading(true);
    setDocsPanelDocs([]);
    try {
      const entityId = (driver as any).driverId || (driver as any).userId || '';
      if (entityId) {
        const response: any = await documentService.getDocuments({ entityType: 'driver', entityId: String(entityId) });
        setDocsPanelDocs(Array.isArray(response?.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setDocsPanelLoading(false);
    }
  };

  const handleDocsPanelUpload = async (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const file = event.target.files?.[0];
    if (!file || !docsPanelDriver) return;
    const entityId = (docsPanelDriver as any).driverId || (docsPanelDriver as any).userId;
    if (!entityId) return;
    setDocsPanelUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('entityType', 'driver');
      uploadData.append('entityId', String(entityId));
      uploadData.append('documentType', docType);
      uploadData.append('name', file.name);
      await documentService.uploadDocument(uploadData);
      const response: any = await documentService.getDocuments({ entityType: 'driver', entityId: String(entityId) });
      setDocsPanelDocs(Array.isArray(response?.data) ? response.data : []);
      toast.success('Belge yüklendi');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Belge yüklenemedi');
    } finally {
      setDocsPanelUploading(false);
      event.target.value = '';
    }
  };

  const handleDocsPanelDelete = async (docId: string) => {
    if (!docsPanelDriver) return;
    if (!(await showConfirm({ title: 'Belge Sil', message: 'Bu belgeyi silmek istediğinize emin misiniz?', confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await documentService.deleteDocument(docId);
      const entityId = (docsPanelDriver as any).driverId || (docsPanelDriver as any).userId;
      if (entityId) {
        const response: any = await documentService.getDocuments({ entityType: 'driver', entityId: String(entityId) });
        setDocsPanelDocs(Array.isArray(response?.data) ? response.data : []);
      }
      toast.success('Belge silindi');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Belge silinemedi');
    }
  };

  const loadCarrierCompanies = async () => {
    try {
      const response: any = await corporateCarrierService.getAll({ status: 'active' });
      setCarrierCompanies(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load corporate carriers:', error);
      setCarrierCompanies([]);
    }
  };

  useEffect(() => {
    void loadDrivers();
    void loadCarrierCompanies();
  }, []);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await driverService.getApplications({ status: 'approved', all: true });
      const data = response.data || response;
      const driversArray = Array.isArray(data) ? data : (data.results || data.rows || data.items || []);

      if (Array.isArray(driversArray)) {
        const nextLocations = driversArray.reduce((acc: Record<string, DriverLocation>, d: any) => {
          if (d.driver_id && d.latest_latitude && d.latest_longitude) {
            acc[String(d.id || d.application_id)] = {
              driver_id: d.driver_id,
              shipment_id: d.latest_shipment_id,
              latitude: d.latest_latitude,
              longitude: d.latest_longitude,
              recorded_at: d.latest_location_at,
            } as DriverLocation;
          }
          return acc;
        }, {});

        const mappedDrivers = driversArray.map((d: any) => {
          const key = d.id?.toString() || d.application_id?.toString() || '';
          return {
            id: key,
            userId: d.user_id?.toString() || d.userId?.toString() || '',
            applicationId: d.id?.toString() || d.application_id?.toString() || '',
            externalTransportAccountUserId: d.external_transport_account_user_id ? String(d.external_transport_account_user_id) : '',
            externalTransportAccountName: d.external_transport_company_title || d.external_transport_account_name || '',
            driverName: d.full_name || d.name || d.driver_name || '',
            phone: d.phone || d.telephone || '',
            lastVerificationCode: d.last_verification_code || '',
            lastVerificationCodeAt: d.last_verification_code_at || null,
            lastVerificationCodeExpiresAt: d.last_verification_code_expires_at || null,
            email: d.email || '',
            tcNo: d.tc_number || d.tc_no || d.tc || '',
            licenseNumber: d.license_number || d.lic_number || d.licence_number || '',
            licenseClass: d.license_class || d.licence_class || 'E',
            vehiclePlate: d.cekici_plaka || d.vehicle_plate || d.plate || '',
            dorsePlaka: d.dorse_plaka || d.trailer_plate || '',
            vehicleType: d.vehicle_type || d.vehicleType || '',
            experience: d.experience_years || d.experience || d.year_experience || 0,
            city: d.city || d.town || '',
            district: d.district || d.neighborhood || '',
            address: d.address || '',
            invoiceTitle: d.invoice_title || d.invoiceTitle || '',
            taxOffice: d.tax_office || d.taxOffice || '',
            taxNumber: d.tax_number || d.taxNumber || '',
            srcCardNumber: d.src_card_number || '',
            srcExpiryDate: d.src_expiry_date || '',
            startDate: d.start_date || '',
            terminationDate: d.termination_date || '',
            isControlResponsible: Number(d.is_control_responsible || 0) === 1,
            harcirahTuru: d.harcirah_turu || 'sabit',
            workDuration: d.work_duration || '',
            currentWeekDrivingHours: d.current_week_hours || '',
            twoWeekDrivingHours: d.two_week_hours || '',
            residenceLatitude: d.residence_latitude || '',
            residenceLongitude: d.residence_longitude || '',
            hasLicense: !!(d.license_number || d.license_class),
            hasSRC: !!d.src_card_number,
            hasPsychotechnic: false,
            hasHealthReport: false,
            status: d.status === 'approved' ? 'active' : d.status === 'rejected' ? 'inactive' : 'suspended',
            source: d.source || 'yuk-ortagim',
            rating: 0,
            totalJobs: 0,
            completedJobs: 0,
            approvedAt: d.reviewed_at || d.created_at || null,
            approvedBy: d.reviewed_by || 'admin',
            createdAt: d.created_at || null,
            updatedAt: d.updated_at || null,
            recordedAt: d.latest_location_at || null,
            driverId: d.driver_id ? String(d.driver_id) : '',
            documentCount: Number(d.document_count || d.docCount || d.documents || 0),
            workiomRecordId: d.workiom_record_id || ''
          };
        });

        setLocations(nextLocations);
        setDrivers(mappedDrivers);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load drivers:', err);
      setError('Sürücüler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const handleDeleteDriver = async (driver: ApprovedDriver) => {
    if (!(await showConfirm({ title: 'Spot Sürücü Sil', message: `${driver.driverName} kaydını silmek istediğinize emin misiniz?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await driverService.deleteApplication(driver.applicationId || driver.id);
      await loadDrivers();
      showAlert({ tone: 'success', title: 'Kayıt Silindi', message: 'Spot sürücü kaydı başarıyla silindi.' });
      if (selectedDriver?.id === driver.id) {
        setSelectedDriver(null);
        setStartEditingDriverId(null);
      }
    } catch (deleteError: any) {
      console.error('Delete spot driver failed:', deleteError);
      showAlert({ tone: 'error', title: 'Silme Başarısız', message: deleteError?.message || 'Spot sürücü kaydı silinemedi.' });
    }
  };

  const openManualLocationModal = (driver: ApprovedDriver) => {
    const loc = locations[driver.id] || locations[(driver as any).applicationId || ''] || null;
    setManualLocationModal({
      driver,
      latitude: loc?.latitude != null ? String(loc.latitude) : '',
      longitude: loc?.longitude != null ? String(loc.longitude) : '',
      saving: false,
    });
  };

  const handleSaveManualLocation = async () => {
    if (!manualLocationModal) return;
    const latitude = Number(manualLocationModal.latitude);
    const longitude = Number(manualLocationModal.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      showAlert({ tone: 'warning', title: 'Geçersiz Konum', message: 'Geçerli enlem ve boylam girin.' });
      return;
    }
    try {
      setManualLocationModal((current) => current ? { ...current, saving: true } : current);
      await driverService.updateApplicationManualLocation(manualLocationModal.driver.applicationId || manualLocationModal.driver.id, { latitude, longitude });
      await loadDrivers();
      showAlert({ tone: 'success', title: 'Konum Güncellendi', message: 'Spot sürücü konumu manuel olarak kaydedildi.' });
      setManualLocationModal(null);
    } catch (err: any) {
      console.error('Manual spot driver location update failed:', err);
      showAlert({ tone: 'error', title: 'Konum Güncellenemedi', message: err?.message || 'Spot sürücü konumu kaydedilemedi.' });
      setManualLocationModal((current) => current ? { ...current, saving: false } : current);
    }
  };

  const openBulkMessageModal = () => {
    if (selectedIds.length === 0) {
      toast.error('En az bir spot sürücü seçin');
      return;
    }
    setBulkMessageModalOpen(true);
  };

  const closeBulkMessageModal = () => {
    setBulkMessageModalOpen(false);
    setBulkMessageText('');
    setBulkMessageChannel('sms');
  };

  const handleBulkSendMessage = async () => {
    if (!bulkMessageText.trim()) {
      toast.error('Mesaj boş olamaz');
      return;
    }
    const selectedDrivers = drivers.filter((d) => selectedIds.includes(d.applicationId || d.id));
    try {
      setBulkMessageSending(true);
      const response: any = await driverService.bulkSendMessage({
        driverIds: selectedDrivers.map((d) => d.applicationId || d.id),
        message: bulkMessageText.trim(),
        channel: bulkMessageChannel,
        targetType: 'external',
      });
      toast.success(response?.message || 'Toplu mesaj gönderildi');
      closeBulkMessageModal();
    } catch (err) {
      console.error('Bulk spot driver message send failed:', err);
      toast.error(err instanceof Error ? err.message : 'Toplu mesaj gönderilemedi');
    } finally {
      setBulkMessageSending(false);
    }
  };

  const isAdminUser = useMemo(() => {
    const effectiveRoles = Array.from(new Set([currentUser?.role, ...(currentUser?.roles || [])].filter(Boolean)));
    return effectiveRoles.includes('admin');
  }, [currentUser?.role, currentUser?.roles]);

  const handleImpersonateDriver = async (driver: any) => {
    const targetUserId = String(driver.userId || '');
    if (!targetUserId) {
      showAlert({ tone: 'warning', title: 'Kullanıcı Bulunamadı', message: 'Bu spot sürücü için bağlı kullanıcı hesabı bulunamadı.' });
      return;
    }
    if (!(await showConfirm({ title: 'Kullanıcı Olarak Giriş Yap', message: `${driver.driverName} hesabına geçilsin mi?`, confirmText: 'Giriş Yap', tone: 'info' }))) return;
    const response = await impersonateUser(targetUserId);
    if (!response.success) {
      showAlert({ tone: 'error', title: 'İşlem Başarısız', message: response.message || 'Kullanıcı oturumu açılamadı.' });
      return;
    }
    const effectiveRoles = Array.from(new Set([response.user?.role, ...(response.user?.roles || [])].filter(Boolean)));
    navigate(effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver') ? '/driver-mobile-home' : '/');
  };

  const handleGenerateVerificationCode = async (driver: ApprovedDriver) => {
    const userId = String(driver.userId || '');
    const applicationId = String(driver.applicationId || driver.id || '');
    if (!userId && !applicationId) {
      toast.error('Spot sürücü kaydı bulunamadı');
      return;
    }
    try {
      const response: any = await authService.generateAdminVerificationCode(userId || undefined, applicationId || undefined);
      const data = response?.data;
      if (!data?.code || !data?.phone) throw new Error(response?.message || 'Doğrulama kodu oluşturulamadı');
      const message = `Yük Ortağım doğrulama kodunuz: ${data.code}. Kod 5 dakika geçerlidir.`;
      await navigator.clipboard?.writeText(message).catch(() => {});
      window.open(`https://web.whatsapp.com/send?phone=90${data.phone}&text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
      toast.success(`Kod oluşturuldu: ${data.code}. Mesaj WhatsApp için panoya kopyalandı.`);
      await loadDrivers();
    } catch (error: any) {
      toast.error(error?.message || 'Doğrulama kodu oluşturulamadı');
    }
  };

  const filteredDrivers = useMemo(() => {
    let result = drivers;
    if (companyIdFilter) {
      result = result.filter((d) => (d as any).externalTransportAccountUserId === companyIdFilter);
    }
    if (filterStatus !== 'all') {
      result = result.filter((d) => d.status === filterStatus);
    }
    return result;
  }, [drivers, companyIdFilter, filterStatus]);

  const getLocationForDriver = (driver: ApprovedDriver) => {
    return locations[driver.id] || locations[(driver as any).applicationId || ''] || null;
  };

  const columns = useMemo<FlexibleDataGridColumn<ApprovedDriver>[]>(() => [
    {
      key: 'driverName',
      label: 'Sürücü',
      width: 220,
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.driverName}</div>
          {row.licenseClass && <div className="text-xs text-gray-500">Ehliyet: {row.licenseClass}</div>}
        </div>
      ),
    },
    {
      key: 'externalTransportAccountName',
      label: 'Kurumsal Nakliye Firması',
      width: 200,
      sortable: true,
      visibleByDefault: false,
    },
    {
      key: 'phone',
      label: 'Telefon',
      width: 140,
      sortable: true,
    },
    {
      key: 'lastVerificationCode',
      label: 'Son Doğrulama Kodu',
      width: 185,
      sortable: true,
      render: (row) => row.lastVerificationCode
        ? <div><div className="font-mono font-semibold text-violet-700">{row.lastVerificationCode}</div><div className="text-[11px] text-gray-500">{row.lastVerificationCodeAt ? new Date(row.lastVerificationCodeAt).toLocaleString('tr-TR') : '-'}</div></div>
        : '-',
    },
    {
      key: 'city',
      label: 'Şehir',
      width: 140,
      sortable: true,
      render: (row) => row.district ? `${row.city} / ${row.district}` : row.city || '-',
    },
    {
      key: 'vehiclePlate',
      label: 'Çekici',
      width: 120,
      sortable: true,
    },
    {
      key: 'dorsePlaka',
      label: 'Dorse',
      width: 120,
      sortable: true,
    },
    {
      key: 'status',
      label: 'Durum',
      width: 110,
      sortable: true,
      render: (row) => {
        const s = STATUS_LABELS[row.status] || STATUS_LABELS['suspended'];
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.bg} ${s.color}`}>{s.label}</span>;
      },
    },
    {
      key: 'source',
      label: 'Kayıt Kaynağı',
      width: 120,
      sortable: true,
      render: (row) => row.source === 'workiom' ? 'Harici' : 'Yük Ortağım',
    },
    {
      key: 'documentCount',
      label: 'Belgeler',
      width: 90,
      sortable: true,
      align: 'center',
      render: (row) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void openDocsPanel(row); }}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${(row.documentCount || 0) > 0 ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
        >
          <FileText className="w-3 h-3" />
          {row.documentCount || 0}
        </button>
      ),
    },
    {
      key: 'location',
      label: 'Konum',
      width: 200,
      render: (row) => {
        const loc = getLocationForDriver(row);
        if (!loc) return <span className="text-gray-400">-</span>;
        return (
          <div className="space-y-1">
            <div className="text-xs text-gray-700">{Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}</div>
            <a href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:text-emerald-800">Haritada Aç</a>
          </div>
        );
      },
    },
    {
      key: 'recordedAt',
      label: 'Son Güncelleme',
      width: 150,
      sortable: true,
      visibleByDefault: false,
      render: (row) => row.recordedAt ? new Date(row.recordedAt).toLocaleString('tr-TR') : '-',
    },
  ], [locations]);

  const rowActions = useMemo(() => (row: ApprovedDriver) => {
    const loc = getLocationForDriver(row);
    return (
      <div className="flex items-center gap-0.5">
        {loc && (
          <a href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noreferrer" title="Google Haritalar" className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800">
            <MapPin className="h-4 w-4" />
          </a>
        )}
        <button type="button" title="Konum Gir" onClick={() => openManualLocationModal(row)} className="rounded p-1.5 text-cyan-600 hover:bg-cyan-50 hover:text-cyan-800">
          <MapPin className="h-4 w-4" />
        </button>
        <button type="button" title="Düzenle" onClick={() => { setSelectedDriver(row); setStartEditingDriverId(row.id); }} className="rounded p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800">
          <Pencil className="h-4 w-4" />
        </button>
        <button type="button" title="Sil" onClick={() => void handleDeleteDriver(row)} className="rounded p-1.5 text-red-600 hover:bg-red-50 hover:text-red-800">
          <Trash2 className="h-4 w-4" />
        </button>
        <button type="button" title="5 dakika geçerli kod oluşturur ve WhatsApp Web'i açar" onClick={() => void handleGenerateVerificationCode(row)} className="rounded p-1.5 text-violet-600 hover:bg-violet-50 hover:text-violet-800">
          <KeyRound className="h-4 w-4" />
        </button>
        {isAdminUser && String(row.userId || '') !== String(currentUser?.id || '') && (
          <button type="button" title="Kullanıcı Olarak Giriş Yap" onClick={() => void handleImpersonateDriver(row)} className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800">
            <LogIn className="h-4 w-4" />
          </button>
        )}
        <button type="button" title="Detay" onClick={() => { setSelectedDriver(row); setStartEditingDriverId(null); }} className="rounded p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800">
          <Eye className="h-4 w-4" />
        </button>
      </div>
    );
  }, [isAdminUser, currentUser?.id, locations]);

  const groupByOptions = useMemo(() => [
    { key: 'status', label: 'Durum', getValue: (row: ApprovedDriver) => STATUS_LABELS[row.status]?.label || row.status },
    { key: 'city', label: 'Şehir', getValue: (row: ApprovedDriver) => row.city || '-' },
    { key: 'vehicleType', label: 'Araç Türü', getValue: (row: ApprovedDriver) => (row as any).vehicleType || '-' },
    { key: 'source', label: 'Kayıt Kaynağı', getValue: (row: ApprovedDriver) => row.source === 'workiom' ? 'Harici' : 'Yük Ortağım' },
  ], []);

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Spot Sürücüler</h1>
            <p className="mt-1 text-gray-500">Dış sürücü başvurularının tamamını görüntüleyin ve yönetin</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button type="button" onClick={openBulkMessageModal} className="workiom-outline-button" disabled={selectedIds.length === 0}>
            <MessageCircle className="w-4 h-4" /> Toplu Whatsapp SMS Gönder ({selectedIds.length})
          </button>
          <button onClick={() => setIsCreateModalOpen(true)} className="workiom-primary-button">
            Yeni Spot Sürücü
          </button>
          <button onClick={() => exportRowsToExcel(filteredDrivers as any[], `spot-suruculer-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'ApprovedDrivers' })} className="workiom-outline-button">
            <Download className="w-4 h-4" /> Dışa Aktar / Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 px-6 py-6 md:grid-cols-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-900">{drivers.length}</div>
          <div className="text-sm text-gray-600">Toplam Sürücü</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-green-600">{drivers.filter(d => d.status === 'active').length}</div>
          <div className="text-sm text-gray-600">Onaylı</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-yellow-600">{drivers.filter(d => d.status === 'suspended').length}</div>
          <div className="text-sm text-gray-600">Beklemede</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-purple-600">{Object.keys(locations).length}</div>
          <div className="text-sm text-gray-600">Canlı Konum</div>
        </div>
      </div>

      <div className="mx-6 mb-4 flex flex-wrap items-center gap-3">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500">
          <option value="all">Tüm Durumlar</option>
          <option value="active">Onaylı</option>
          <option value="suspended">Beklemede</option>
          <option value="inactive">Reddedildi</option>
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Sürücüler yükleniyor...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 mx-6">
          <p className="text-red-600">{error}</p>
          <button onClick={() => void loadDrivers()} className="text-blue-600 underline mt-2">Tekrar dene</button>
        </div>
      )}

      {!loading && (
        <div className="mx-6 mb-6">
          <FlexibleDataGrid
            rows={filteredDrivers}
            columns={columns}
            getRowKey={(row) => row.applicationId || row.id}
            loading={loading}
            emptyMessage="Spot sürücü bulunamadı"
            searchable
            selectable
            initialPageSize={50}
            pageSizeOptions={[25, 50, 100, 250]}
            groupByOptions={groupByOptions}
            rowActions={rowActions}
            onSelectionChange={(rows) => setSelectedIds(rows.map((r) => r.applicationId || r.id))}
          />
        </div>
      )}

      {selectedDriver && (
        <DriverDetailModal
          driver={selectedDriver}
          carrierCompanies={carrierCompanies}
          initialEditing={startEditingDriverId === selectedDriver.id}
          onSaved={() => void loadDrivers()}
          onClose={() => { setSelectedDriver(null); setStartEditingDriverId(null); }}
        />
      )}

      {isCreateModalOpen && (
        <DriverDetailModal
          driver={{ id: 'new', applicationId: '', externalTransportAccountUserId: '', externalTransportAccountName: '', driverName: '', phone: '', email: '', tcNo: '', licenseNumber: '', licenseClass: 'E', vehiclePlate: '', dorsePlaka: '', vehicleType: '', experience: 0, city: '', district: '', address: '', hasLicense: false, hasSRC: false, hasPsychotechnic: false, hasHealthReport: false, status: 'active', rating: 0, totalJobs: 0, completedJobs: 0, approvedAt: new Date().toISOString(), approvedBy: 'admin', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), source: 'yuk-ortagim' } as any}
          carrierCompanies={carrierCompanies}
          initialEditing
          onSaved={() => void loadDrivers()}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      {bulkMessageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Toplu Whatsapp / SMS Gönder</h2>
              <p className="mt-1 text-sm text-gray-500">Seçili {drivers.filter((d) => selectedIds.includes(d.applicationId || d.id)).length} spot sürücüye mesaj gönderin.</p>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Kanal</label>
                <select value={bulkMessageChannel} onChange={(e) => setBulkMessageChannel(e.target.value as 'sms' | 'whatsapp')} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp (QR Kod)</option>
                </select>
              </div>
              {bulkMessageChannel === 'whatsapp' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <div className="font-medium">WhatsApp Web ile gönderim</div>
                  <div className="mt-1">Bu gönderim QR kod ile bağlanan WhatsApp Web oturumu üzerinden yapılır.</div>
                  <button type="button" onClick={() => window.open('/whatsapp-web', '_blank', 'noopener,noreferrer')} className="mt-3 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-emerald-700 hover:bg-emerald-100">
                    WhatsApp Web / QR Kod Ekranını Aç
                  </button>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Mesaj</label>
                <textarea value={bulkMessageText} onChange={(e) => setBulkMessageText(e.target.value)} rows={6} className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Gönderilecek mesajı yazın" />
              </div>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                {drivers.filter((d) => selectedIds.includes(d.applicationId || d.id)).map((driver) => (
                  <div key={driver.applicationId || driver.id}>{driver.driverName || '-'} - {driver.phone || '-'}</div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
              <button type="button" onClick={closeBulkMessageModal} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">İptal</button>
              <button type="button" onClick={handleBulkSendMessage} disabled={bulkMessageSending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
                <Send className="h-4 w-4" /> {bulkMessageSending ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {manualLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !manualLocationModal.saving && setManualLocationModal(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Manuel Konum Gir</div>
              <div className="mt-1 text-sm text-gray-500">{manualLocationModal.driver.driverName}</div>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Enlem</label>
                <input value={manualLocationModal.latitude} onChange={(e) => setManualLocationModal((current) => current ? { ...current, latitude: e.target.value } : current)} placeholder="41.015137" className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Boylam</label>
                <input value={manualLocationModal.longitude} onChange={(e) => setManualLocationModal((current) => current ? { ...current, longitude: e.target.value } : current)} placeholder="28.979530" className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button type="button" onClick={() => setManualLocationModal(null)} disabled={manualLocationModal.saving} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700">İptal</button>
              <button type="button" onClick={() => void handleSaveManualLocation()} disabled={manualLocationModal.saving} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-60">{manualLocationModal.saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}

      {docsPanelDriver && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDocsPanelDriver(null)}>
          <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Belgeler</h3>
                <p className="text-sm text-gray-500">{docsPanelDriver.driverName}</p>
              </div>
              <button type="button" onClick={() => setDocsPanelDriver(null)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              {docsPanelLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(DOCUMENT_LABELS).map(([type, label]) => {
                    const existing = docsPanelDocs.find((d: any) => d.document_type === type);
                    return (
                      <div key={type} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900">{label}</span>
                            {existing ? <CheckCircle className="w-4 h-4 text-green-500" /> : <span className="text-xs text-red-500">Yüklenmedi</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            {existing && (
                              <>
                                <a href={resolveFileUrl(existing.file_path)} target="_blank" rel="noreferrer" title="Görüntüle / İndir" className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"><Eye className="w-4 h-4" /></a>
                                <a href={resolveFileUrl(existing.file_path)} download title="İndir" className="rounded p-1.5 text-blue-600 hover:bg-blue-50"><Download className="w-4 h-4" /></a>
                                <button type="button" onClick={() => void handleDocsPanelDelete(String(existing.id))} title="Sil" className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                            <label title={existing ? 'Değiştir' : 'Yükle'} className="cursor-pointer rounded p-1.5 text-blue-600 hover:bg-blue-50">
                              <Upload className="w-4 h-4" />
                              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => void handleDocsPanelUpload(e, type)} disabled={docsPanelUploading} />
                            </label>
                          </div>
                        </div>
                        {existing && (
                          <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                            <span>{existing.file_name}</span>
                            {existing.expires_at && <span>Geçerlilik: {new Date(existing.expires_at).toLocaleDateString('tr-TR')}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {docsPanelUploading && <div className="text-sm text-blue-600">Belge yükleniyor...</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Driver Detail Modal
function DriverDetailModal({ driver, carrierCompanies, onClose, initialEditing = false, onSaved }: { driver: ApprovedDriver; carrierCompanies: CorporateCarrier[]; onClose: () => void; initialEditing?: boolean; onSaved?: () => Promise<void> | void }) {
  const { showAlert } = useAppAlert();
  const [userAccount, setUserAccount] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [formData, setFormData] = useState<any>({});
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [docMeta, setDocMeta] = useState<Record<string, { file_name: string; expires_at: string }>>({});
  const [carrierVehicles, setCarrierVehicles] = useState<CarrierVehicleOption[]>([]);
  const [carrierVehiclesLoading, setCarrierVehiclesLoading] = useState(false);

  const requiredDocs = [
    'tax-plate', 'license', 'tractor-license', 'trailer-license', 'src',
    'vehicle-insurance', 'contract', 'carrier-authority',  'psychotechnic'
  ];
  const driverDocumentEntityId = (driver as any).driverId || userAccount?.id || null;
  const carrierListId = `approved-driver-carriers-${driver.id}`;
  const selectedCarrierName = carrierCompanies.find((carrier) => String(carrier.id) === String(formData.external_transport_account_user_id || ''))?.companyTitle || (driver as any).externalTransportAccountName || '';

  useEffect(() => {
    const carrierId = String(formData.external_transport_account_user_id || '').trim();
    if (!carrierId) {
      setCarrierVehicles([]);
      return;
    }

    let cancelled = false;
    const loadCarrierVehicles = async () => {
      try {
        setCarrierVehiclesLoading(true);
        const response: any = await vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: carrierId, status: 'active' });
        const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        if (cancelled) return;
        setCarrierVehicles(items.map((vehicle: any) => ({
          id: String(vehicle.id),
          plate: vehicle.cekici_plaka || vehicle.plate || '',
          trailerPlate: vehicle.dorse_plaka || vehicle.linked_trailer_plate || '',
          vehicleType: vehicle.vehicle_type || '',
        })).filter((vehicle: CarrierVehicleOption) => vehicle.plate));
      } catch (error) {
        console.error('Carrier vehicles load failed:', error);
        if (!cancelled) setCarrierVehicles([]);
      } finally {
        if (!cancelled) setCarrierVehiclesLoading(false);
      }
    };

    void loadCarrierVehicles();

    return () => {
      cancelled = true;
    };
  }, [formData.external_transport_account_user_id]);

  const loadDocuments = async (entityId?: string | number) => {
    if (!entityId) return;
    try {
      const response: any = await documentService.getDocuments({ entityType: 'driver', entityId: String(entityId) });
      const docs = Array.isArray(response?.data) ? response.data : [];
      setDocuments(docs);
      setDocMeta(docs.reduce((acc: any, doc: any) => {
        acc[String(doc.id)] = {
          file_name: doc.file_name || doc.name || '',
          expires_at: doc.expires_at || '',
        };
        return acc;
      }, {}));
    } catch (error) {
      console.error('Failed to load driver documents:', error);
      setDocuments([]);
    }
  };

  useEffect(() => {
    const fetchUserAccount = async () => {
      try {
        const response = await userService.getUsers();
        const users = (response.data || response) as any[];
        const found = users.find((u: any) => normalizePhone(u.phone) === normalizePhone(driver.phone) || String(u.email || '').trim().toLocaleLowerCase('tr-TR') === String(driver.email || '').trim().toLocaleLowerCase('tr-TR') || u.approvedDriverId === driver.id || u.driverId === driver.id);
        setUserAccount(found || null);
        setFormData({
          full_name: driver.driverName || '',
          phone: driver.phone || '',
          email: driver.email || '',
          tc_number: driver.tcNo || '',
          city: driver.city || '',
          district: driver.district || '',
          address: driver.address || '',
          cekici_plaka: driver.vehiclePlate || '',
          dorse_plaka: (driver as any).dorsePlaka || '',
          vehicle_type: driver.vehicleType || '',
          license_number: driver.licenseNumber || '',
          license_class: driver.licenseClass || '',
          experience_years: driver.experience || 0,
          invoice_title: (driver as any).invoiceTitle || '',
          tax_office: (driver as any).taxOffice || '',
          tax_number: (driver as any).taxNumber || '',
          external_transport_account_user_id: (driver as any).externalTransportAccountUserId || '',
          external_transport_account_name: (driver as any).externalTransportAccountName || '',
          src_card_number: (driver as any).srcCardNumber || '',
          src_expiry_date: String((driver as any).srcExpiryDate || '').split('T')[0],
          start_date: String((driver as any).startDate || (driver as any).start_date || '').split('T')[0],
          termination_date: String((driver as any).terminationDate || (driver as any).termination_date || '').split('T')[0],
          is_control_responsible: Boolean((driver as any).isControlResponsible),
          harcirah_turu: (driver as any).harcirahTuru || 'sabit',
          work_duration: (driver as any).workDuration || '',
          current_week_hours: (driver as any).currentWeekDrivingHours || '',
          two_week_hours: (driver as any).twoWeekDrivingHours || '',
          residence_latitude: (driver as any).residenceLatitude || '',
          residence_longitude: (driver as any).residenceLongitude || '',
        });
        if ((driver as any).driverId) {
          await loadDocuments((driver as any).driverId);
        } else if (found?.id) {
          await loadDocuments(found.id);
        }
      } catch (err) {
        console.error('Failed to fetch user account:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserAccount();
  }, [driver.id]);

  useEffect(() => {
    setIsEditing(initialEditing);
  }, [initialEditing]);

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    const file = event.target.files?.[0];
    const driverEntityId = (driver as any).driverId || userAccount?.id;
    if (!file || !driverEntityId) return;

    try {
      setUploading(true);
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('entityType', 'driver');
      uploadData.append('entityId', String(driverEntityId));
      uploadData.append('documentType', documentType);
      uploadData.append('name', file.name);

      let ocrResult: any = null;
      if (documentType === 'tax-plate') {
        try {
          const ocrResponse = await documentService.ocrTaxPlate(file);
          if (ocrResponse.success && ocrResponse.data) {
            ocrResult = ocrResponse.data;
            uploadData.append('ocrMetadata', JSON.stringify({
              invoiceTitle: ocrResult.invoiceTitle || formData.invoice_title || null,
              taxOffice: ocrResult.taxOffice || formData.tax_office || null,
              taxNumber: ocrResult.taxNumber || formData.tax_number || null,
              confidence: ocrResult.confidence,
              source: 'ocr-tesseract-admin',
              rawText: ocrResult.rawText?.substring(0, 200),
            }));

            const taxUpdates: any = {};
            if (ocrResult.invoiceTitle) {
              taxUpdates.invoice_title = ocrResult.invoiceTitle;
              setFormData((prev: any) => ({ ...prev, invoice_title: ocrResult.invoiceTitle }));
            }
            if (ocrResult.taxOffice) {
              taxUpdates.tax_office = ocrResult.taxOffice;
              setFormData((prev: any) => ({ ...prev, tax_office: ocrResult.taxOffice }));
            }
            if (ocrResult.taxNumber) {
              taxUpdates.tax_number = ocrResult.taxNumber;
              setFormData((prev: any) => ({ ...prev, tax_number: ocrResult.taxNumber }));
            }
            if (Object.keys(taxUpdates).length > 0) {
              await driverService.updateApplication(driver.id, taxUpdates);
            }
          }
        } catch (ocrError) {
          console.warn('OCR failed, using profile data:', ocrError);
          uploadData.append('ocrMetadata', JSON.stringify({
            invoiceTitle: formData.invoice_title || null,
            taxOffice: formData.tax_office || null,
            taxNumber: formData.tax_number || null,
            source: 'admin-driver-tax-plate',
          }));
        }
      }

      await documentService.uploadDocument(uploadData);
      await loadDocuments(driverEntityId);
    } catch (error) {
      console.error('Document upload failed:', error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleUpdateDocument = async (documentId: string) => {
    try {
      const meta = docMeta[documentId];
      await documentService.updateDocument(documentId, {
        file_name: meta.file_name,
        expires_at: meta.expires_at || null,
      });
      setEditingDocId(null);
      if (driverDocumentEntityId) await loadDocuments(driverDocumentEntityId);
    } catch (error) {
      console.error('Document update failed:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await documentService.deleteDocument(documentId);
      if (driverDocumentEntityId) await loadDocuments(driverDocumentEntityId);
    } catch (error) {
      console.error('Document delete failed:', error);
    }
  };

  const handleSaveDriver = async () => {
    try {
      if (!isValidPlate(formData.cekici_plaka)) return showAlert({ tone: 'warning', title: 'Geçersiz Plaka', message: 'Çekici plaka formatı geçersiz.' });
      if (!isValidPlate(formData.dorse_plaka)) return showAlert({ tone: 'warning', title: 'Geçersiz Plaka', message: 'Dorse plaka formatı geçersiz.' });
      const normalizedPayload = {
        ...formData,
        full_name: normalizeUpper(formData.full_name),
        phone: normalizePhone(formData.phone),
        city: normalizeUpper(formData.city),
        district: normalizeUpper(formData.district),
        license_class: normalizeUpper(formData.license_class),
        cekici_plaka: normalizePlate(formData.cekici_plaka),
        dorse_plaka: normalizePlate(formData.dorse_plaka),
        vehicle_type: normalizeUpper(formData.vehicle_type),
        invoice_title: normalizeUpper(formData.invoice_title),
        tax_office: normalizeUpper(formData.tax_office),
        external_transport_account_user_id: formData.external_transport_account_user_id || null,
        src_card_number: formData.src_card_number || null,
        src_expiry_date: formData.src_expiry_date || null,
        start_date: formData.start_date || null,
        termination_date: formData.termination_date || null,
        is_control_responsible: formData.is_control_responsible ? 1 : 0,
        harcirah_turu: formData.harcirah_turu || 'sabit',
        work_duration: formData.work_duration || null,
        current_week_hours: formData.current_week_hours || null,
        two_week_hours: formData.two_week_hours || null,
        residence_latitude: formData.residence_latitude || null,
        residence_longitude: formData.residence_longitude || null,
      };
      if (driver.id === 'new') {
        await driverService.createApplication(normalizedPayload);
      } else {
        await driverService.updateApplication(driver.id, normalizedPayload);
      }
      if (userAccount?.id && driver.id !== 'new') {
        await userService.updateUser(userAccount.id, {
          full_name: formData.full_name,
          phone: formData.phone,
          email: formData.email,
        });
      }
      setIsEditing(false);
      await onSaved?.();
      showAlert({ tone: 'success', title: driver.id === 'new' ? 'Kayıt Oluşturuldu' : 'Kayıt Güncellendi', message: driver.id === 'new' ? 'Spot sürücü kaydı başarıyla oluşturuldu.' : 'Spot sürücü bilgileri başarıyla güncellendi.' });
      onClose();
    } catch (error) {
      console.error('Driver application update failed:', error);
      showAlert({ tone: 'error', title: 'Kaydetme Başarısız', message: 'Spot sürücü bilgileri kaydedilemedi.' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[90vh] sm:max-w-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-gray-900">{driver.id === 'new' ? 'Yeni Spot Sürücü' : (isEditing ? 'Sürücüyü Düzenle' : 'Sürücü Detayları')}</h2>
          <div className="flex items-center gap-3">
            {driver.id !== 'new' && <button onClick={() => setIsEditing((v) => !v)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">{isEditing ? 'Vazgeç' : 'Düzenle'}</button>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XCircle className="w-6 h-6" /></button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {userAccount && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="font-semibold text-blue-900">{userAccount.username}</span>
                <span className="text-blue-700">{userAccount.email}</span>
                <span className={`font-medium ${userAccount.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>{userAccount.status === 'active' ? 'Aktif' : 'Pasif'}</span>
              </div>
            </div>
          )}

          {!userAccount && driver.id !== 'new' && (
            <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
              <p className="text-sm text-yellow-700">Kullanıcı hesabı henüz oluşturulmamış. Başvurular sayfasından onaylayarak oluşturabilirsiniz.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Kişisel Bilgiler */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad {!isEditing && '*'}</label>
              {isEditing ? <input value={formData.full_name || ''} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.driverName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              {isEditing ? <input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
              {isEditing ? <input value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.email || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TC Kimlik No</label>
              {isEditing ? <input value={formData.tc_number || ''} onChange={(e) => setFormData({ ...formData, tc_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.tcNo || '-'}</p>}
            </div>

            {/* Ehliyet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ehliyet Numarası</label>
              {isEditing ? <input value={formData.license_number || ''} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.licenseNumber || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ehliyet Sınıfı</label>
              {isEditing ? <input value={formData.license_class || ''} onChange={(e) => setFormData({ ...formData, license_class: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.licenseClass || '-'}</p>}
            </div>

            {/* Kurumsal Nakliye */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kurumsal Nakliye Firması</label>
              {isEditing ? (
                <>
                  <input value={formData.external_transport_account_name || selectedCarrierName || ''} list={carrierListId} onChange={(event) => { const value = event.target.value; const carrier = carrierCompanies.find((item) => item.companyTitle === value || item.fullName === value); setFormData({ ...formData, external_transport_account_name: value, external_transport_account_user_id: carrier ? carrier.id : '' }); }} placeholder="Firma seçin" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <datalist id={carrierListId}>{carrierCompanies.map((carrier) => <option key={carrier.id} value={carrier.companyTitle || carrier.fullName} />)}</datalist>
                </>
              ) : <p className="text-sm text-gray-900 py-2">{(driver as any).externalTransportAccountName || '-'}</p>}
            </div>
            {isEditing && formData.external_transport_account_user_id ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Firmaya Bağlı Çekici</label>
                <select value={formData.cekici_plaka || ''} onChange={(event) => { const vehicle = carrierVehicles.find((item) => item.plate === event.target.value); setFormData({ ...formData, cekici_plaka: event.target.value, dorse_plaka: vehicle?.trailerPlate || formData.dorse_plaka || '', vehicle_type: vehicle?.vehicleType ? normalizeUpper(vehicle.vehicleType) : formData.vehicle_type || '' }); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Çekici seçin</option>
                  {carrierVehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.plate}>{vehicle.plate}{vehicle.trailerPlate ? ` / ${vehicle.trailerPlate}` : ''}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">{carrierVehiclesLoading ? 'Yükleniyor...' : carrierVehicles.length === 0 ? 'Aktif çekici bulunamadı.' : 'Dorse plakası otomatik doldurulur.'}</p>
              </div>
            ) : null}

            {/* Araç */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Çekici Plaka</label>
              {isEditing ? <input value={formData.cekici_plaka || ''} onChange={(e) => setFormData({ ...formData, cekici_plaka: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.vehiclePlate || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dorse Plaka</label>
              {isEditing ? <input value={formData.dorse_plaka || ''} onChange={(e) => setFormData({ ...formData, dorse_plaka: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.dorsePlaka || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Araç Tipi</label>
              {isEditing ? <input value={formData.vehicle_type || ''} onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.vehicleType || '-'}</p>}
            </div>

            {/* Adres */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İkamet Şehir</label>
              {isEditing ? <input value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.city || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İlçe</label>
              {isEditing ? <input value={formData.district || ''} onChange={(e) => setFormData({ ...formData, district: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.district || '-'}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
              {isEditing ? <textarea value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{driver.address || '-'}</p>}
            </div>

            {/* Fatura / Vergi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fatura Ünvanı</label>
              {isEditing ? <input value={formData.invoice_title || ''} onChange={(e) => setFormData({ ...formData, invoice_title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.invoice_title || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vergi Dairesi</label>
              {isEditing ? <input value={formData.tax_office || ''} onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.tax_office || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vergi No</label>
              {isEditing ? <input value={formData.tax_number || ''} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.tax_number || '-'}</p>}
            </div>

            {/* SRC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SRC Kart Numarası</label>
              {isEditing ? <input value={formData.src_card_number || ''} onChange={(e) => setFormData({ ...formData, src_card_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.src_card_number || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SRC Bitiş Tarihi</label>
              {isEditing ? <input type="date" value={formData.src_expiry_date || ''} onChange={(e) => setFormData({ ...formData, src_expiry_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.src_expiry_date ? new Date(formData.src_expiry_date).toLocaleDateString('tr-TR') : '-'}</p>}
            </div>

            {/* Çalışma */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İşe Başlama Tarihi</label>
              {isEditing ? <input type="date" value={formData.start_date || ''} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.start_date ? new Date(formData.start_date).toLocaleDateString('tr-TR') : '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İşten Çıkış Tarihi</label>
              {isEditing ? <input type="date" value={formData.termination_date || ''} onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.termination_date ? new Date(formData.termination_date).toLocaleDateString('tr-TR') : '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Çalışma Süresi</label>
              {isEditing ? <input value={formData.work_duration || ''} onChange={(e) => setFormData({ ...formData, work_duration: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.work_duration || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bu Hafta Çalışma Saati</label>
              {isEditing ? <input value={formData.current_week_hours || ''} onChange={(e) => setFormData({ ...formData, current_week_hours: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.current_week_hours || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Son 2 Hafta Çalışma Saati</label>
              {isEditing ? <input value={formData.two_week_hours || ''} onChange={(e) => setFormData({ ...formData, two_week_hours: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.two_week_hours || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Harcırah Türü</label>
              {isEditing ? (
                <select value={formData.harcirah_turu || 'sabit'} onChange={(e) => setFormData({ ...formData, harcirah_turu: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="sabit">Sabit Harcırah</option>
                  <option value="aylik">Aylık Harcırah</option>
                  <option value="dinamik">Dinamik Harcırah</option>
                </select>
              ) : <p className="text-sm text-gray-900 py-2">{formData.harcirah_turu === 'aylik' ? 'Aylık' : formData.harcirah_turu === 'dinamik' ? 'Dinamik' : 'Sabit'}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Kontrol Sorumlusu</label>
              {isEditing ? (
                <label className="inline-flex items-center gap-3 rounded-lg border border-gray-300 px-3 py-2">
                  <input type="checkbox" checked={Boolean(formData.is_control_responsible)} onChange={(e) => setFormData({ ...formData, is_control_responsible: e.target.checked ? 1 : 0 })} className="h-4 w-4 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Araç kontrol formlarında kontrol eden olarak seçilebilsin</span>
                </label>
              ) : <p className="text-sm text-gray-900 py-2">{formData.is_control_responsible ? 'Evet' : 'Hayır'}</p>}
            </div>

            {/* İkamet Koordinatları */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İkamet Enlem</label>
              {isEditing ? <input type="number" step="0.00000001" value={formData.residence_latitude || ''} onChange={(e) => setFormData({ ...formData, residence_latitude: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.residence_latitude || '-'}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İkamet Boylam</label>
              {isEditing ? <input type="number" step="0.00000001" value={formData.residence_longitude || ''} onChange={(e) => setFormData({ ...formData, residence_longitude: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /> : <p className="text-sm text-gray-900 py-2">{formData.residence_longitude || '-'}</p>}
            </div>
          </div>

          {/* Belgeler */}
          {userAccount && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Belgeler</h3>
              <div className="space-y-2">
                {requiredDocs.map((docType) => {
                  const existing = documents.find((doc: any) => doc.document_type === docType);
                  return (
                    <div key={docType} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-600" />
                          <span className="font-medium text-sm text-gray-900">{DOCUMENT_LABELS[docType] || docType}</span>
                          {existing ? <CheckCircle className="w-4 h-4 text-green-600" /> : <span className="text-xs text-red-600">Yüklenmedi</span>}
                        </div>
                        <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                          <Upload className="w-3 h-3" />
                          {existing ? 'Değiştir' : 'Yükle'}
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleUploadDocument(e, docType)} />
                        </label>
                      </div>
                      {existing && (
                        <div className="space-y-2">
                          {editingDocId === String(existing.id) ? (
                            <>
                              <input value={docMeta[String(existing.id)]?.file_name || ''} onChange={(e) => setDocMeta((prev) => ({ ...prev, [String(existing.id)]: { ...prev[String(existing.id)], file_name: e.target.value } }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                              <input type="date" value={docMeta[String(existing.id)]?.expires_at ? String(docMeta[String(existing.id)].expires_at).slice(0,10) : ''} onChange={(e) => setDocMeta((prev) => ({ ...prev, [String(existing.id)]: { ...prev[String(existing.id)], expires_at: e.target.value } }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                              <div className="flex gap-2">
                                <button onClick={() => handleUpdateDocument(String(existing.id))} className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg flex items-center gap-1"><Save className="w-3 h-3" />Kaydet</button>
                                <button onClick={() => setEditingDocId(null)} className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg">Vazgeç</button>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-between gap-3 text-xs text-gray-600">
                              <div>
                                <div>{existing.file_name}</div>
                                {existing.expires_at && <div>Son geçerlilik: {new Date(existing.expires_at).toLocaleDateString('tr-TR')}</div>}
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => window.open(resolveFileUrl(existing.file_path), '_blank', 'noopener,noreferrer')} className="text-emerald-600 hover:text-emerald-800">Görüntüle</button>
                                <button onClick={() => setEditingDocId(String(existing.id))} className="text-blue-600 hover:text-blue-800">Düzenle</button>
                                <button onClick={() => handleDeleteDocument(String(existing.id))} className="text-red-600 hover:text-red-800 flex items-center gap-1"><Trash2 className="w-3 h-3" />Sil</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {uploading && <div className="text-sm text-blue-600">Belge yükleniyor...</div>}
              </div>
            </div>
          )}

          {/* Onay Bilgileri */}
          {driver.id !== 'new' && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Onay Bilgileri</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Onaylayan: <span className="text-gray-900">{driver.approvedBy}</span></div>
                <div>Onay Tarihi: <span className="text-gray-900">{new Date(driver.approvedAt).toLocaleDateString('tr-TR')}</span></div>
                {driver.applicationId && <div>Başvuru No: <span className="text-gray-900">{driver.applicationId}</span></div>}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
          {isEditing && <button onClick={handleSaveDriver} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Kaydet</button>}
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">Kapat</button>
        </div>
      </div>
    </div>
  );
}
